import Close from "@mui/icons-material/Close";
import Edit from "@mui/icons-material/Edit";
import {
  Alert,
  Button,
  Divider,
  IconButton,
  MenuItem,
  Tooltip,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  GENERATOR_STATUS_CORRECTION_FIELDS,
  getFieldLabel,
  isManifestLevelCorrectionField,
} from "encamp-shared/src/waste/discrepancyFieldMeta";
import { ManifestCorrectionValue } from "routes/Customer/Waste/Manifests/ManifestCorrectionValue";

import { CorrectionDraft, CorrectionFieldDraft } from "./correctionDraft";
import {
  CorrectionReviewLines,
  CorrectionSelection,
  SelectedCorrectionField,
} from "./correctionReviewSelection";
import { CorrectionValueEditor } from "./CorrectionValueEditor";

export function ManifestCorrectionReviewFields({
  lines,
  selection,
  onSelectionChange,
  selected,
  draft,
  onChange,
  processedManifestId,
}: {
  lines: CorrectionReviewLines;
  selection: CorrectionSelection;
  onSelectionChange: (selection: CorrectionSelection) => void;
  selected: SelectedCorrectionField;
  draft: CorrectionDraft;
  onChange: (draft: CorrectionDraft) => void;
  processedManifestId: string;
}) {
  const manifestLevel = isManifestLevelCorrectionField(selection.field);
  const existing = selected
    ? draft.fields.find(
        (item) =>
          item.field === selected.field && item.target.wasteLineUnifiedId === selected.lineId
      )
    : undefined;
  const duplicate = existing != null && !selection.editing;
  const clearSelection = () => {
    onSelectionChange({ field: "", lineId: "" });
  };
  return (
    <Stack spacing={3} sx={{ p: 1 }}>
      <Stack spacing={2}>
        <Typography variant="subtitle2">
          {selection.editing ? "Edit correction" : "What needs correcting?"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Choose a field that affects generator status and check its value against the manifest.
        </Typography>
        <TextField
          select
          label="Field"
          value={selection.field}
          disabled={selection.editing}
          onChange={(event) =>
            onSelectionChange({ ...selection, field: event.target.value, editing: false })
          }
          size="small"
          fullWidth
        >
          {GENERATOR_STATUS_CORRECTION_FIELDS.map((item) => (
            <MenuItem
              key={item}
              value={item}
              disabled={lines.length === 0 && !isManifestLevelCorrectionField(item)}
            >
              {getFieldLabel(item)}
            </MenuItem>
          ))}
        </TextField>
        {selection.field && !manifestLevel && (
          <TextField
            select
            label="Waste line"
            value={selection.lineId}
            disabled={selection.editing}
            onChange={(event) =>
              onSelectionChange({ ...selection, lineId: event.target.value, editing: false })
            }
            size="small"
            fullWidth
          >
            {lines.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                Line {item.wasteLineNumber}
              </MenuItem>
            ))}
          </TextField>
        )}
        {lines.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No waste lines are available. You can still correct the generator EPA ID.
          </Typography>
        )}
        {duplicate && (
          <Alert severity="info">This field already has a correction. Edit below.</Alert>
        )}
        {selected && !duplicate && (
          <CorrectionValueEditor
            key={`${selected.field}:${selected.lineId}`}
            {...selected}
            existing={selection.editing ? existing : undefined}
            onSave={(correction) => {
              const index = draft.fields.findIndex(
                (item) =>
                  item.field === correction.field &&
                  item.target.wasteLineUnifiedId === correction.target.wasteLineUnifiedId
              );
              const fields = [...draft.fields];
              if (index < 0) fields.push(correction);
              else fields[index] = correction;
              onChange({ ...draft, fields });
              clearSelection();
            }}
            onCancel={clearSelection}
            processedManifestId={processedManifestId}
          />
        )}
        {(!selected || duplicate) && selection.field && (
          <Button
            aria-label="Cancel correction"
            onClick={clearSelection}
            sx={{ alignSelf: "flex-end" }}
          >
            Cancel
          </Button>
        )}
      </Stack>
      {draft.fields.length > 0 && (
        <>
          <Divider />
          <CorrectionDraftList
            draft={draft}
            onChange={(nextDraft) => {
              onChange(nextDraft);
              if (existing && !nextDraft.fields.includes(existing)) clearSelection();
            }}
            onEdit={(item) => {
              onSelectionChange({
                field: item.field,
                lineId: item.target.wasteLineUnifiedId ?? "",
                editing: true,
              });
            }}
          />
        </>
      )}
    </Stack>
  );
}

function CorrectionDraftList({
  draft,
  onChange,
  onEdit,
}: {
  draft: CorrectionDraft;
  onChange: (draft: CorrectionDraft) => void;
  onEdit: (item: CorrectionFieldDraft) => void;
}) {
  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">Corrections ({draft.fields.length})</Typography>
      {draft.fields.map((item) => {
        return (
          <Stack
            key={`${item.target.wasteLineUnifiedId}:${item.field}`}
            direction="row"
            spacing={1}
          >
            <Stack sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={500}>
                {item.selectionLabel}
              </Typography>
              <ManifestCorrectionValue field={item.field} value={item.correctValue} />
            </Stack>
            <Tooltip title="Edit correction">
              <IconButton
                size="small"
                sx={{ alignSelf: "center" }}
                onClick={() => onEdit(item)}
                aria-label={`Edit ${item.selectionLabel}`}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton
              size="small"
              sx={{ alignSelf: "center" }}
              aria-label={`Remove ${item.selectionLabel}`}
              onClick={() =>
                onChange({ ...draft, fields: draft.fields.filter((entry) => entry !== item) })
              }
            >
              <Close fontSize="small" />
            </IconButton>
          </Stack>
        );
      })}
      <Typography variant="caption" color="text.secondary">
        These corrections will be saved together after you add a note.
      </Typography>
    </Stack>
  );
}
